import React, { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useHistory } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import toast from "react-hot-toast";
import Select from "react-select";

import { createBrandSchema } from "../../schema";
import {
  Input,
  Button,
  Map,
  UploadField,
  ErrorMessage,
  Card,
  MainContent,
  Grid,
  Buttons,
} from "../../components";
import {
  fetchClients,
  fetchBrandCategories,
  fetchCountries,
  fetchCountryInfo,
  createBrand,
} from "../../redux/actions";
import {
  getCountryOptions,
  getCityOptions,
  getCategoryOptions,
  getClientOptions,
} from "../../utils";
import { dismissToast, somethignWentWrong } from "../../helpers";

export const CreateBrand = () => {
  const [address, setAddress] = useState("");
  const [lat, setLat] = useState(0);
  const [lng, setLng] = useState(0);
  const history = useHistory();
  const dispatch = useDispatch();
  const { ref, inView } = useInView();
  const clients = useSelector(({ clients }) => clients.clients);
  const brandCategories = useSelector(({ brands }) => brands.categories);
  const countryManagementState = useSelector(
    ({ countryManagement }) => countryManagement
  );
  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(createBrandSchema),
  });

  const handleChange = useCallback(
    (selectedOption) => {
      dispatch(fetchCountryInfo(selectedOption.id));
      setValue("cityName", "");
    },
    [setValue, dispatch]
  );

  const handleCreateBrand = useCallback(
    (data) => {
      dismissToast();
      dispatch(createBrand(data, address, lat, lng))
        .then((res) => {
          if (res.status === 201) {
            setTimeout(() => history.push("/brand-management"), [2000]);
            toast.success(`${data.name} has been created Successfully`);
          } else if (res.status === 409) {
            toast.error("Brand already exist");
          } else if (res.status === 401) {
            history.push("/");
          } else {
            somethignWentWrong();
          }
        })
        .catch(() => somethignWentWrong());
    },
    [dispatch, address, lat, lng, history]
  );

  useEffect(() => {
    dispatch(fetchCountries());
    dispatch(fetchClients());
    dispatch(fetchBrandCategories());
  }, [dispatch]);

  return (
    <MainContent>
      <form onSubmit={handleSubmit(handleCreateBrand)} noValidate>
        <div className="container_mobile">
          <div className="page-header" ref={ref}>
            <h1>Brand Management</h1>
            <div className={inView ? `` : `sticky-action-bar`}>
              <Buttons noMargin>
                <Link to="/brand-management" className="btn btn-secondary">
                  Back
                </Link>
                <Button type="submit" variant="primary">
                  Create
                </Button>
              </Buttons>
            </div>
          </div>
        </div>
        <div className="cards_wrapper">
          <Card title="Create Brand">
            <Grid columns={3}>
              <Input
                label="Brand Name"
                type="text"
                placeholder="Brand Name"
                name="name"
                register={register}
                errors={errors}
              />
              <Input
                label="Email Address"
                type="email"
                placeholder="Email Address"
                name="email"
                register={register}
                errors={errors}
              />
              <div className="input_holder">
                <label>Client Name</label>
                <Controller
                  name="clientName"
                  control={control}
                  render={({ field }) => (
                    <Select
                      menuPortalTarget={document.body}
                      {...field}
                      options={getClientOptions(clients)}
                      placeholder="Select Client"
                      className="kmmrce-select-container"
                      classNamePrefix="kmmrce-select"
                    />
                  )}
                />
                {errors && errors["clientName"] && (
                  <ErrorMessage text="Client Name is Required" />
                )}
              </div>
            </Grid>
            <Grid columns={2}>
              <Input
                label="Brand Mobile Number"
                type="tel"
                placeholder="Brand Mobile Number"
                name="mobileNumber"
                register={register}
                errors={errors}
              />
              <div className="input_holder">
                <label>Brand Category</label>
                <Controller
                  name="category"
                  errors={errors}
                  control={control}
                  render={({ field }) => (
                    <Select
                      menuPortalTarget={document.body}
                      {...field}
                      options={getCategoryOptions(brandCategories)}
                      placeholder="Select Category"
                      className="kmmrce-select-container"
                      classNamePrefix="kmmrce-select"
                    />
                  )}
                />
                {errors && errors["category"] && (
                  <ErrorMessage text="Brand Category is Required" />
                )}
              </div>
            </Grid>
            <UploadField
              label="Brand Logo"
              name="logo"
              errors={errors}
              register={register}
            />
          </Card>
          <Card title="Location">
            <Grid columns={3}>
              <div className="input_holder">
                <label>Country Name</label>
                <Controller
                  name="countryName"
                  errors={errors}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      menuPortalTarget={document.body}
                      options={getCountryOptions(
                        countryManagementState.countries
                      )}
                      placeholder="Select Country"
                      onChange={(e) => {
                        onChange(e);
                        handleChange(e);
                      }}
                      value={value ? value : ""}
                      className="kmmrce-select-container"
                      classNamePrefix="kmmrce-select"
                    />
                  )}
                />
                {errors && errors["countryName"] && (
                  <ErrorMessage text="Country is Required" />
                )}
              </div>

              <div className="input_holder">
                <label>City Name</label>
                <Controller
                  name="cityName"
                  control={control}
                  render={({ field }) => (
                    <Select
                      menuPortalTarget={document.body}
                      {...field}
                      options={getCityOptions(
                        countryManagementState?.countryInfo?.cities
                      )}
                      isLoading={countryManagementState?.countryInfoLoading}
                      loadingMessage={() => "Loading..."}
                      placeholder="Select City"
                      className="kmmrce-select-container"
                      classNamePrefix="kmmrce-select"
                    />
                  )}
                />
                {errors && errors["cityName"] && (
                  <ErrorMessage text="City is Required" />
                )}
              </div>
              <Input
                label="Brand P.O. Box"
                type="text"
                placeholder="Brand P.O. Box"
                name="poBox"
                register={register}
                errors={errors}
              />
            </Grid>
            <Map
              addressInfo={(address, lat, lng) => {
                setAddress(address);
                setLat(lat);
                setLng(lng);
              }}
            />
          </Card>
        </div>
      </form>
    </MainContent>
  );
};

export default CreateBrand;
